import React, { useState } from 'react';
import { useAgents } from './hooks';
import AgentsContent from './AgentsContent';
import { AgentModal, AgentDetailModal } from '.';
import { DeleteAgentConfirmDialog } from './DeleteAgentConfirmDialog';
import { Agent } from '../../../types';
import { useAdminTranslation } from '../../../modules/i18n';

const AgentsContainer: React.FC = () => {
  const { t } = useAdminTranslation();

  const {
    // State
    agents,
    loading,
    error,
    showAgentModal,
    editingAgent,
    selectedAgent,
    showAgentDetailModal,
    showHidden,

    // Actions
    handleCreateAgent,
    handleEditAgent,
    handleDeleteAgent,
    handleViewAgentDetail,
    handleCloseModal,
    handleCloseDetailModal,
    handleSaveAgent,
    handleToggleHidden,
    setShowHidden,
    refetchAgents
  } = useAgents();

  // State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; agent: Agent | null }>({
    isOpen: false,
    agent: null
  });

  // Handle delete with confirmation
  const handleDeleteWithConfirm = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    if (agent) {
      setDeleteConfirm({ isOpen: true, agent });
    }
  };

  const confirmDelete = async () => {
    if (deleteConfirm.agent) {
      await handleDeleteAgent(deleteConfirm.agent.id);
      setDeleteConfirm({ isOpen: false, agent: null });
    }
  };

  if (loading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex min-h-[400px] flex-col items-center justify-center text-center'>
        <div className='mb-4 text-red-500'>
          <svg className='mx-auto h-16 w-16' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        </div>
        <h3 className='mb-2 text-lg font-medium text-gray-900 dark:text-dark-text'>{t('content.agents.errors.loadTitle')}</h3>
        <p className='mb-4 text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.errors.loadMessage')}</p>
        <button onClick={refetchAgents} className='rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/80'>
          {t('content.agents.errors.retryButton')}
        </button>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col gap-6'>
      <AgentsContent
        agents={agents}
        showHidden={showHidden}
        onShowHiddenChange={setShowHidden}
        onCreateAgent={handleCreateAgent}
        onEditAgent={handleEditAgent}
        onDeleteAgent={handleDeleteWithConfirm}
        onViewAgentDetail={handleViewAgentDetail}
        onToggleHidden={handleToggleHidden}
      />

      {/* Agent Modal */}
      {showAgentModal && (
        <AgentModal
          isOpen={showAgentModal}
          onClose={handleCloseModal}
          onSave={handleSaveAgent}
          editingAgent={editingAgent}
          onAgentCreated={refetchAgents}
        />
      )}

      {/* Agent Detail Modal */}
      {showAgentDetailModal && selectedAgent && (
        <AgentDetailModal isOpen={showAgentDetailModal} onClose={handleCloseDetailModal} agent={selectedAgent} />
      )}

      {/* Delete Confirmation Dialog (pre-flights for in-flight tasks) */}
      <DeleteAgentConfirmDialog
        isOpen={deleteConfirm.isOpen}
        agent={deleteConfirm.agent}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, agent: null })}
      />
    </div>
  );
};

export default AgentsContainer;
